import {
  Component,
  Input,
  ViewChild,
  ElementRef,
  OnInit,
  OnChanges,
  OnDestroy,
  inject,
  effect,
} from '@angular/core';
import { BarChartData } from '../../interfaces/global/chart';
import { Chart, BarController, BarElement } from 'chart.js';
import { colors } from '../../constants/tableColors';
import { logo as base64Logo } from './logo-base64';
import jsPDF from 'jspdf';
import { CommonModule } from '@angular/common';
import { NgIcon } from '@ng-icons/core';
import { ChartComponent } from '../chart/chart.component';
import { DictionaryService } from '../../services/dictionary.service';
import { TableContext } from '../../services/table/table-context.service';

@Component({
  selector: 'app-bar-chart',
  imports: [CommonModule, NgIcon],
  templateUrl: './bar-chart.component.html',
  styleUrl: './bar-chart.component.scss',
})
export class BarChartComponent
  extends ChartComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() name: string = '';
  @Input() metadata: string = '';
  @Input() title: string = 'Bar Chart';

  private _chartElm: ElementRef | undefined;
  private _isInitializing = false;

  @ViewChild('barchartcanvas')
  set chartElm(value: ElementRef | undefined) {
    this._chartElm = value;
    if (value) {
      setTimeout(() => {
        this.refreshChartState();
      }, 0);
    }
  }

  get chartElm(): ElementRef | undefined {
    return this._chartElm;
  }

  protected dictionaryService = inject(DictionaryService);
  protected tableContext = inject(TableContext);
  private lastState: 'empty' | 'data' | undefined;
  data = this.tableContext.chartData;

  constructor() {
    super();
    this.chartType = 'bar';
    Chart.register(BarController, BarElement);

    effect(() => {
      // Register dependencies
      this.data();
      // Attempt to refresh
      if (this.chartElm) {
        this.refreshChartState();
      }
    });
  }

  refreshChartState = () => {
    if (!this.chartElm) return;

    const rect = this.chartElm.nativeElement.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      requestAnimationFrame(() => this.refreshChartState());
      return;
    }

    // Prevent recursive calls or overlapping updates
    if (this._isInitializing) return;

    const data = this.tableContext.chartData();

    let currentState: 'empty' | 'data';
    if (data && Object.keys(data).length > 0) currentState = 'data';
    else currentState = 'empty';

    // Only destroy if the state TYPE changed (e.g. Data -> Empty)
    if (this.chart && this.lastState !== currentState) {
      this.chart.destroy();
      this.chart = undefined;
    }

    if (!this.chart) {
      // Use requestAnimationFrame to smooth the creation
      this._isInitializing = true;
      requestAnimationFrame(() => {
        this.initializeChart(); // Create instance
        this.applyOptions(currentState); // Apply options
        this.chart?.update(); // Render
        this.lastState = currentState;
        this._isInitializing = false;
      });
    } else {
      // If chart exists, just update it immediately
      this.applyOptions(currentState);
      this.chart.update();
      this.lastState = currentState;
    }
  };

  applyOptions(state: 'loading' | 'empty' | 'data') {
    if (state === 'loading') this.setLoadingOptions();
    else if (state === 'data') this.setDataOptions();
    else this.setEmptyOptions();
  }

  initializeChart = () => {
    if (this.chartElm) {
      this.chart = new Chart(this.chartElm.nativeElement, {
        type: 'bar',
        data: { labels: [], datasets: [] },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          resizeDelay: 0,
          plugins: { datalabels: { display: false } },
        },
      });
    }
  };

  override setLoadingOptions(): void {
    console.log('Setting Bar Chart Loading Options...');
    if (!this.chart) return;

    const loadingData = Array(15)
      .fill(0)
      .map(() => Number(Math.random().toFixed(2)) * 100);

    this.chart.data = {
      labels: ['', '', '', '', '', '', '', '', '', '', '', '', '', '', ''],
      datasets: [
        {
          data: loadingData,
          backgroundColor: this.colorMap['--boulder'],
          borderRadius: 5,
          offset: 8,
          maxBarThickness: 100,
        },
      ],
    };

    this.chart.options = {
      responsive: true,
      maintainAspectRatio: false,
      aspectRatio: 3 / 4,
      plugins: {
        title: {
          display: true,
          color: this.colorMap['--text'],
          font: {
            weight: 'bold',
            size: this.fontSize,
          },
        },
        datalabels: { display: false },
        legend: {
          display: false,
        },
        tooltip: {
          enabled: false,
        },
      },
      animations: {
        backgroundColor: {
          loop: true,
          duration: 1000,
          easing: 'easeInOutSine',
          type: 'color',
          from: 'rgba(200, 200, 200, 0.4)',
          to: 'rgba(200, 200, 200, 0.8)',
        },
      },
      scales: {
        x: {
          grid: {
            display: false,
          },
          ticks: {
            display: false,
          },
        },
        y: {
          grid: {
            display: false,
          },
          ticks: {
            display: false,
          },
        },
      },
    };

    this.chart.update();
  }

  override setDataOptions(): void {
    if (!this.chart) return;

    this.chart.data = {
      labels: Object.keys(this.data()),
      datasets: this.convertDataToDatasets(this.data()),
    };

    this.chart.options = {
      responsive: true,
      maintainAspectRatio: false,
      aspectRatio: 3 / 4,
      plugins: {
        datalabels: {
          display: false,
        },
        legend: {
          display: true, // <--- EXPLICITLY TURN IT BACK ON
          title: {
            display: true,
            text: this.title,
            color: this.colorMap['--text'],
            font: {
              weight: 'bold',
              size: this.fontSize,
            },
          },
        },
        tooltip: {
          enabled: true, // Ensure tooltips are back
        },
      },
      animations: {
        y: {
          duration: 500,
          easing: 'easeInOutQuad',
          delay: (context: any) => {
            if (context.type === 'data' && context.mode === 'default') {
              return context.dataIndex * 25 + context.datasetIndex * 10;
            }
            return 0;
          },
        },
      },
      scales: {
        x: {
          grid: {
            lineWidth: 2,
          },
          ticks: {
            display: true,
          },
        },
        y: {
          grid: {
            lineWidth: 2,
          },
          ticks: {
            display: true,
          },
        },
      },
    };

    console.log('Bar Chart Options:', this.chart.options);

    this.chart.update();
  }

  override setEmptyOptions(): void {
    if (!this.chart) return;

    this.chart.data = {
      labels: [''],
      datasets: [
        {
          data: [1],
          backgroundColor: this.colorMap['--boulder'],
          borderRadius: 5,
        },
      ],
    };

    this.chart.options = {
      responsive: true,
      maintainAspectRatio: false,
      aspectRatio: 3 / 4,
      plugins: {
        title: {
          display: true,
          text: 'No Data Available',
          color: this.colorMap['--text'],
          font: {
            weight: 'bold',
            size: this.fontSize,
          },
        },
        legend: { display: false },
        tooltip: { enabled: false },
        datalabels: { display: false },
      },
      scales: {
        x: {
          grid: {
            lineWidth: 2,
          },
          ticks: {
            display: false,
          },
        },
        y: {
          grid: {
            lineWidth: 2,
          },
          ticks: {
            display: false,
          },
        },
      },
    };
  }

  exportChart = () => {
    const image = this.chart?.toBase64Image();

    if (image) {
      const chart = new Image();
      chart.src = image;

      const logo = new Image();
      logo.src = base64Logo;

      const padding = 30;
      const headerHeight = 55;

      const chartOffset = this.metadata != '' ? 16 : 0;

      chart.onload = () => {
        const chartWidth = chart.width;
        const chartHeight = chart.height;

        const pageWidth = 700;
        const pageHeight =
          (pageWidth / chartWidth) * chartHeight +
          headerHeight +
          2 * padding +
          chartOffset * 2;

        const doc = new jsPDF('landscape', 'px', [pageWidth, pageHeight]);

        const ratio = (pageWidth - 2 * padding) / chartWidth;

        const newWidth = chartWidth * ratio;
        const newHeight = chartHeight * ratio;

        const chartXPos = (pageWidth - newWidth) / 2;
        const chartYPos = headerHeight + padding + chartOffset;

        doc.addImage(image, 'PNG', chartXPos, chartYPos, newWidth, newHeight);

        doc.setFontSize(18);
        doc.text('Report Export', padding, padding + 5);

        doc.setDrawColor(169, 169, 169);
        doc.line(
          padding,
          headerHeight,
          doc.internal.pageSize.getWidth() - padding,
          headerHeight
        );

        if (this.metadata != '') {
          doc.setFontSize(9);
          doc.setTextColor('#787878');
          doc.text(this.metadata, padding, headerHeight + 5 + 5);
        }

        const logoWidth = 65;
        const logoHeight = (logo.height / logo.width) * logoWidth;

        doc.addImage(
          logo,
          'PNG',
          doc.internal.pageSize.getWidth() - padding - logoWidth,
          12,
          logoWidth,
          logoHeight
        );

        if (this.name != '') {
          doc.save(this.name + '.pdf');
        } else {
          doc.save(this.id + '.pdf');
        }
      };
    }
  };

  private convertDataToDatasets(data: BarChartData): any[] {
    const datasets: any[] = [];

    Object.entries(data).map(([rowName, columns], rowIndex) => {
      columns.map((col, index) => {
        if (rowIndex === 0) {
          datasets.push({
            label: col.name,
            data: [col.value],
            backgroundColor: colors[index % colors.length],
            borderRadius: 5,
            offset: 8,
            maxBarThickness: 100,
          });
        } else {
          datasets[index].data.push(col.value);
        }
      });
    });

    return datasets;
  }

  override isDataAvailable = () => {
    const isTrue = this.data() && Object.keys(this.data()).length > 0;
    return isTrue;
  };

  isNoData = () => {
    return !this.data() || Object.keys(this.data()).length === 0;
  };
}
